import { useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { api, getApiErrorMessage } from '../api/client';
import type { CardPhase } from '../lib/dispatch-phase-cards';

export interface PhaseClockVars {
  jobId: string;
  phase: CardPhase;
  action: 'start' | 'end';
  /** For the toast — the job number the dispatcher is looking at. */
  jobNumber?: string;
}

/**
 * Start or end one phase of a job from the dispatch board.
 *
 * This replaces a fire-and-forget `await api.dispatch.updateJob(...)` that had
 * no catch: a 409 or a 403 left the card exactly as it was, with nothing on
 * screen to say the tap had been refused. A refusal now names itself, in the
 * API's own words where it wrote some.
 *
 * The equipment keys are invalidated beside the board because the FIRST phase
 * start on a job moves reserved gear into use server-side.
 */
export function usePhaseClockMutation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ jobId, phase, action }: PhaseClockVars) =>
      action === 'start'
        ? api.dispatch.startPhase(jobId, phase)
        : api.dispatch.endPhase(jobId, phase),
    onError: (err, { phase, action, jobNumber }) => {
      const what = `${action === 'start' ? 'start' : 'end'} the ${phase}`;
      toast.error(getApiErrorMessage(err, `Could not ${what}${jobNumber ? ` on ${jobNumber}` : ''}.`));
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ['dispatch-jobs'] });
      queryClient.invalidateQueries({ queryKey: ['equipment'] });
      queryClient.invalidateQueries({ queryKey: ['equipment-cert-summary'] });
      queryClient.invalidateQueries({ queryKey: ['home-equipment'] });
      queryClient.invalidateQueries({ queryKey: ['field-my-equipment'] });
      queryClient.invalidateQueries({ queryKey: ['equipment-report'] });
    },
  });
}
